import { DesktopCaptureFailure } from '@/lib/telemetry/desktopCaptureErrors';
import { logger } from '@/utils/logger/logger';
import { sanitizeError } from '@heidi/runtime/logger';

export type StartPhase =
  | 'active_upload_probe'
  | 'liveness_probe'
  | 'device_enumeration_initial'
  | 'device_enumeration_validate'
  | 'flag_gates'
  | 'mic_permission'
  | 'screen_permission'
  | 'register_recording'
  | 'release_idle_stream'
  | 'auth_token'
  | 'native_start';

/**
 * Hard deadline per phase. A failed start costs the clinician more than a longer
 * wait, so the steps that cross the network or wait on the user get generous
 * margins. Mic permission waits on the macOS dialog; a retry joins that same
 * request, so a timeout never stacks a second one. Registration and the token
 * fetch have no other bound: `t11nClient` sets no axios timeout, and the v1
 * call's `timeout` and `axios-retry` options are sent as the request body, not
 * as config (DSK-1354). `native_start` has none because Rust owns its timebox.
 */
export const START_PHASE_DEADLINE_MS: Record<StartPhase, number | null> = {
  active_upload_probe: 5000,
  liveness_probe: 8000,
  device_enumeration_initial: 30000,
  device_enumeration_validate: 30000,
  flag_gates: 10000,
  mic_permission: 60000,
  screen_permission: 15000,
  register_recording: 60000,
  release_idle_stream: 10000,
  auth_token: 20000,
  native_start: null,
};

export class StartPhaseTimeoutError extends Error {
  constructor(readonly phase: StartPhase) {
    super(`Recording start step timed out: ${phase}`);
    this.name = DesktopCaptureFailure.START_STEP_TIMEOUT;
  }
}

export const isStartPhaseTimeout = (error: unknown): error is StartPhaseTimeoutError =>
  error instanceof StartPhaseTimeoutError;

/**
 * Runs the pre-start steps of one start under per-phase deadlines. A step gets
 * an `AbortSignal` that fires when its deadline passes, so a call that honors
 * it stops instead of landing after the start has rolled back. `flush` logs one
 * summary line and `metadata` gives the same durations for the click's attempt
 * tracking. Ids, names and durations only.
 */
export const createStartPhaseRecorder = (sessionId: string) => {
  const phases: Partial<Record<StartPhase, number>> = {};
  const failures = new Map<StartPhase, unknown>();
  const fallbacks: StartPhase[] = [];

  const run = async <T>(
    phase: StartPhase,
    step: (signal: AbortSignal) => Promise<T>
  ): Promise<T> => {
    const startedMs = performance.now();
    const deadlineMs = START_PHASE_DEADLINE_MS[phase];
    const controller = new AbortController();
    let deadlineTimer: ReturnType<typeof setTimeout> | undefined;
    try {
      const result = await (deadlineMs === null
        ? step(controller.signal)
        : Promise.race([
            step(controller.signal),
            new Promise<never>((_, reject) => {
              deadlineTimer = setTimeout(() => {
                logger.warn('DESKTOP_RECORDING_START_PHASE_TIMED_OUT', {
                  sessionId,
                  phase,
                  deadlineMs,
                });
                controller.abort();
                reject(new StartPhaseTimeoutError(phase));
              }, deadlineMs);
            }),
          ]));
      return result;
    } catch (error) {
      failures.set(phase, error);
      if (!isStartPhaseTimeout(error)) {
        logger.warn('DESKTOP_RECORDING_START_PHASE_FAILED', {
          sessionId,
          phase,
          errorName: error instanceof Error ? error.name : typeof error,
          ...sanitizeError(error),
        });
      }
      throw error;
    } finally {
      clearTimeout(deadlineTimer);
      phases[phase] = Math.round(performance.now() - startedMs);
    }
  };

  /**
   * Like `run`, but a timeout yields `fallback`, for steps the start can do
   * without. The step is listed in the summary and metadata as a fallback, since
   * the start then runs degraded.
   */
  const runOr = async <T>(
    phase: StartPhase,
    step: (signal: AbortSignal) => Promise<T>,
    fallback: T
  ): Promise<T> => {
    try {
      return await run(phase, step);
    } catch (error) {
      if (!isStartPhaseTimeout(error)) throw error;
      fallbacks.push(phase);
      return fallback;
    }
  };

  /** Logs the summary and returns the phase whose error ended the start, if any. */
  const flush = (endedWith?: unknown): StartPhase | undefined => {
    const failedPhase =
      endedWith === undefined
        ? undefined
        : [...failures].find(([, error]) => error === endedWith)?.[0];
    logger.info('DESKTOP_RECORDING_START_PHASES', {
      sessionId,
      phases,
      failedPhase,
      fallbacks,
    });
    return failedPhase;
  };

  /**
   * Flat durations for the attempt's finish metadata. Not checkpoints: those
   * would split the existing `timer_started -> audio_started` pair.
   */
  const metadata = (failedPhase?: StartPhase): Record<string, number | string> => ({
    ...Object.fromEntries(
      Object.entries(phases).map(([phase, ms]) => [`start_phase_${phase}_ms`, ms])
    ),
    ...(failedPhase ? { failed_phase: failedPhase } : {}),
    ...(fallbacks.length > 0 ? { start_fallbacks: fallbacks.join(',') } : {}),
  });

  return { run, runOr, flush, metadata };
};
